// Per-unit title-deed (tulajdoni lap) panel. Every row here is a deed:
// no kind switching, no AUTHORIZATION/OTHER. Mounted by PropertyView
// under each unit card so the deed lives with the ingatlan it
// describes. Property-level documents (authorization, other) stay in
// the separate property-scoped DocumentsPanel.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { filePickerHtml, wireDropZones } from '../core/filePicker.js';

export class UnitDocumentsPanel extends Component {
  constructor({ unitId, canManage }) {
    super();
    this.unitId = unitId;
    this.canManage = !!canManage;
    this.state = {
      documents: null,
      error: null,
      busy: false,
      uploadOpen: false,
      panelOpen: true,        // <details open> state for the section fold
      importBusyId: null,
      importNotice: null,
    };
  }

  template() {
    const d = this.state.documents;
    const count = Array.isArray(d) ? d.length : 0;
    const title = `${t('unit.documents.title')} · ${count}`;
    const meta = String(t('unit.documents.section_meta') || t('unit.documents.subtitle') || '').toUpperCase();
    const open = this.state.panelOpen !== false;
    const uploadLabel = `+ ${String(t('unit.documents.upload') || 'Upload deed').toUpperCase()}`;
    return `
      <details class="unit-section-fold" data-unit-documents-panel${open ? ' open' : ''}>
        <summary>
          <span>${esc(title)}</span>
          <span class="unit-section-fold-meta">${esc(meta)}</span>
        </summary>
        <div class="unit-section-fold-body">
          ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
          ${this.state.importNotice ? `<div class="info-banner">${esc(this.state.importNotice)}</div>` : ''}
          ${d === null
            ? `<div class="muted">${esc(t('unit.documents.loading'))}</div>`
            : count === 0
              ? `<div class="empty" style="background: var(--color-bg-card); border: 1px dashed var(--color-border); border-radius: 8px; padding: 16px; text-align: center; color: var(--color-text-faint);">${esc(t('unit.documents.empty'))}</div>`
              : `<div class="stack">${d.map((r) => this._rowHtml(r)).join('')}</div>`}
          ${this.canManage
            ? (this.state.uploadOpen
                ? this._uploadFormHtml()
                : `<button type="button" class="fold-add-trigger" data-action="open-upload"><span class="fold-add-trigger-summary">${esc(uploadLabel)}</span></button>`)
            : ''}
        </div>
      </details>
    `;
  }

  _uploadFormHtml() {
    const heading = `+ ${String(t('unit.documents.upload') || 'Upload deed').toUpperCase()}`;
    return `
      <form class="fold-add-trigger open" data-upload-form enctype="multipart/form-data">
        <div class="fold-add-trigger-summary">${esc(heading)}</div>
        <div data-form-error></div>
        <div class="field">
          <label>${esc(t('unit.documents.label_field'))} <span class="faint">(${esc(t('common.optional'))})</span></label>
          <input class="input" name="label" maxlength="190" placeholder="${esc(t('unit.documents.label_placeholder'))}" />
        </div>
        <div class="field">
          <label>${esc(t('unit.documents.file'))}</label>
          ${filePickerHtml({
            inputAttrs: 'name="file" accept="image/jpeg,image/png,image/webp,image/heic,image/gif,application/pdf" required data-role="ud-file"',
            disabled: this.state.busy,
            variant: 'drop-zone',
            subLabel: t('unit.documents.file_sub'),
          })}
        </div>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button class="btn btn-ghost" type="button" data-action="cancel-upload">${esc(t('common.cancel'))}</button>
          <button class="btn btn-primary" type="submit" ${this.state.busy ? 'disabled' : ''}>
            ${this.state.busy ? '<span class="spinner"></span> ' : ''}${esc(this.state.busy ? t('unit.documents.uploading') : t('unit.documents.upload_submit'))}
          </button>
        </div>
      </form>
    `;
  }

  _rowHtml(row) {
    const sizeKb = Math.max(1, Math.round((row.size_bytes || 0) / 1024));
    const f = row.extracted_fields || null;
    const hrsz = f?.helyrajzi_szam ? esc(f.helyrajzi_szam) : null;
    const owners = Array.isArray(f?.owners) ? f.owners.filter((o) => o && (o.is_current !== false)) : [];
    return `
      <div class="card stack" data-doc-id="${esc(row.id)}">
        <div class="row" style="align-items:center; gap:var(--space-3)">
          ${icon('file-text', { class: 'list-leading icon-faint' })}
          <div class="stack-2 grow">
            <strong>${esc(t('unit.documents.row_title'))}${row.label ? ' — ' + esc(row.label) : ''}</strong>
            <span class="faint">${esc(row.original_name)} · ${sizeKb} KB</span>
            ${hrsz ? `<span class="faint">${esc(t('unit.documents.parcel_label'))} ${hrsz}</span>` : ''}
            ${this._jobBadgeHtml(row)}
          </div>
          <button class="btn btn-ghost btn-icon" data-action="download-doc" data-id="${esc(row.id)}"
                  aria-label="${esc(t('common.download'))}">${icon('download', { size: 'sm' })}</button>
          ${this.canManage ? `
            <button class="btn btn-ghost btn-icon" data-action="delete-doc" data-id="${esc(row.id)}"
                    aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
        </div>
        ${owners.length > 0 ? `
          <div class="stack-2">
            <span class="faint">${esc(t('unit.documents.owners_extracted'))}</span>
            <ol style="margin:0; padding-left:1.25em">
              ${owners.map((o) => `<li><strong>${esc(o.name || '—')}</strong>${o.share ? ` <span class="badge">${esc(o.share)}</span>` : ''}</li>`).join('')}
            </ol>
            ${this.canManage ? `<div><button class="btn btn-primary btn-sm" data-action="import-owners" data-id="${esc(row.id)}" ${this.state.importBusyId === row.id ? 'disabled' : ''}>
              ${icon('download', { size: 'sm' })}<span>${esc(this.state.importBusyId === row.id ? t('unit.documents.importing') : t('unit.documents.import_owners'))}</span>
            </button></div>` : ''}
          </div>` : ''}
      </div>
    `;
  }

  /**
   * Per-row badge describing the OCR job state. Hidden once a job is
   * "done" AND we already have extracted_fields on the document — at
   * that point the rendered owners/parcel block conveys the same
   * information without the badge clutter.
   */
  _jobBadgeHtml(row) {
    const job = row.ocr_job || null;
    const hasFields = row.extracted_fields && Object.keys(row.extracted_fields).length > 0;
    if (!job) return '';
    if (job.status === 'done' && hasFields) return '';
    if (job.status === 'pending') {
      return `<span class="badge"><span class="spinner"></span> ${esc(t('unit.documents.job.queued'))}</span>`;
    }
    if (job.status === 'running') {
      return `<span class="badge"><span class="spinner"></span> ${esc(t('unit.documents.job.running'))}</span>`;
    }
    if (job.status === 'done') {
      return `<span class="badge badge-success">${esc(t('unit.documents.job.done'))}</span>`;
    }
    if (job.status === 'failed') {
      const msg = job.error_message ? ` — ${esc(job.error_message)}` : '';
      return `<span class="badge badge-warning">${esc(t('unit.documents.job.failed'))}${msg}</span>`;
    }
    return '';
  }

  destroy() {
    for (const u of (this._blobUrls || [])) {
      try { URL.revokeObjectURL(u); } catch {}
    }
    this._blobUrls = [];
    if (this._pollTimer) { clearTimeout(this._pollTimer); this._pollTimer = null; }
  }

  mount() {
    this._blobUrls = this._blobUrls || [];
    this.on('[data-action="open-upload"]', 'click', () => this.setState({ uploadOpen: true, panelOpen: true, error: null, importNotice: null }));
    this.on('[data-action="cancel-upload"]', 'click', () => this.setState({ uploadOpen: false, error: null }));
    if (this.el && this.el.tagName === 'DETAILS') {
      this.el.addEventListener('toggle', (e) => {
        const isOpen = !!(e.currentTarget && e.currentTarget.open);
        if (isOpen !== (this.state.panelOpen !== false)) {
          this.state.panelOpen = isOpen;
        }
      });
    }
    this.on('[data-action="delete-doc"]', 'click', (e) => this._delete(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="download-doc"]', 'click', (e) => this._download(e.currentTarget.getAttribute('data-id')));
    this.on('[data-action="import-owners"]', 'click', (e) => this._importOwners(e.currentTarget.getAttribute('data-id')));
    const form = this.el.querySelector('[data-upload-form]');
    if (form) form.addEventListener('submit', (e) => this._submit(e));
    wireDropZones(this.el);
    if (this.state.documents === null) this._load();
    this._schedulePollIfNeeded();
  }

  /**
   * Poll the documents list while any row has a pending/running OCR
   * job, then stop. The interval ramps from 2s → 10s so we don't hammer
   * the API on long jobs but stay responsive at the start. Cleared in
   * destroy() and re-scheduled after each render.
   */
  _schedulePollIfNeeded() {
    if (this._pollTimer) { clearTimeout(this._pollTimer); this._pollTimer = null; }
    const active = (this.state.documents || []).some((d) => {
      const s = d.ocr_job?.status;
      return s === 'pending' || s === 'running';
    });
    if (!active) return;
    const nextDelay = Math.min(10000, (this._pollDelay || 2000) * 1.4);
    this._pollDelay = nextDelay;
    this._pollTimer = setTimeout(() => {
      this._pollTimer = null;
      this._load(/* silent */ true);
    }, nextDelay);
  }

  async _load(silent = false) {
    try {
      const res = await api.get(`/units/${this.unitId}/documents`);
      // Reset poll backoff every time we land on a list — a brand-new
      // pending job after a "done" should start polling fast again.
      this._pollDelay = silent ? this._pollDelay : 2000;
      this.setState({ documents: res.data || [], error: null });
    } catch (err) {
      // Silent polls swallow errors so a transient blip doesn't replace
      // the existing rows with a banner. Real (non-silent) loads still
      // surface failures.
      if (silent) {
        this._schedulePollIfNeeded();
        return;
      }
      this.setState({ documents: [], error: err instanceof ApiError ? err.message : t('unit.documents.errors.load') });
    }
  }

  async _submit(e) {
    e.preventDefault();
    if (this.state.busy) return;
    const form = e.target;
    const fd = new FormData();
    const file = form.elements.file?.files?.[0];
    if (!file) {
      this.setState({ error: t('unit.documents.errors.file_required') });
      return;
    }
    fd.append('file', file);
    const label = (form.elements.label?.value || '').trim();
    if (label !== '') fd.append('label', label);
    this.setState({ busy: true, error: null });
    try {
      await api.post(`/units/${this.unitId}/documents`, fd);
      await this._load();
      this.setState({ busy: false, uploadOpen: false });
    } catch (err) {
      this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('unit.documents.errors.upload') });
    }
  }

  async _delete(id) {
    if (!id) return;
    if (!confirm(t('unit.documents.delete_confirm'))) return;
    try {
      await api.delete(`/unit-documents/${encodeURIComponent(id)}`);
      this.setState({ documents: (this.state.documents || []).filter((d) => d.id !== id), error: null });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('unit.documents.errors.delete') });
    }
  }

  async _download(id) {
    const row = (this.state.documents || []).find((d) => d.id === id);
    if (!row) return;
    try {
      const blob = await api.get(`/unit-documents/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      this._blobUrls.push(url);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.original_name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('unit.documents.errors.download') });
    }
  }

  async _importOwners(docId) {
    if (!docId) return;
    this.setState({ importBusyId: docId, importNotice: null, error: null });
    try {
      const res = await api.post(`/units/${this.unitId}/external-owners/from-document/${encodeURIComponent(docId)}`, {});
      const created = Array.isArray(res?.data) ? res.data.length : 0;
      this.setState({
        importBusyId: null,
        importNotice: created > 0
          ? t('unit.documents.import_summary.ok', { count: String(created) })
          : t('unit.documents.import_summary.none'),
      });
    } catch (err) {
      this.setState({ importBusyId: null, error: err instanceof ApiError ? err.message : t('unit.documents.import_summary.failed') });
    }
  }
}

export function attachUnitDocumentsPanel(rootEl, props) {
  // jsdom (test env) doesn't ship CSS.escape; fall back to a literal
  // attribute-value escape that's enough for the UUID-shaped unit ids
  // we use.
  const esc = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
    ? CSS.escape
    : (s) => String(s).replace(/["\\]/g, '\\$&');
  const slot = rootEl.querySelector(`[data-unit-documents-slot="${esc(props.unitId)}"]`);
  if (!slot) return null;
  const panel = new UnitDocumentsPanel(props);
  panel.render(slot);
  return panel;
}
